// useSessionWatch — hlídá platnost přihlášení bez čekání na chybu ze serveru
// a prodlužuje relaci, když uživatel s aplikací pracuje (viz doc/plans/session-expiry.md).
//  - časovač na `exp` tokenu + kontrola při návratu na kartu (visibilitychange/focus)
//    → prošlý token = useAuthStore.sessionExpired() (login modal, kontext zůstává)
//  - aktivita (pointerdown/keydown/wheel, max. 1× za minutu) a tokenu zbývá méně než
//    polovina životnosti → POST /auth/refresh a nový token (bez invalidace dat)
// Volá se z App.jsx (běží vždy, nezávisle na routě).
import { useEffect } from 'react'
import { useAuthStore } from '../store/useAuthStore'
import { authApi } from '../api/auth'
import { tokenTimes } from '../utils/jwt'

const ACTIVITY_THROTTLE_MS = 60 * 1000
// Rezerva — token se bere jako prošlý chvíli před `exp`, ať request nedorazí na server už neplatný.
const EXPIRY_SKEW_MS = 5 * 1000
// setTimeout přetéká nad ~24,8 dne — delší čekání se rozdělí.
const MAX_TIMEOUT_MS = 2 ** 31 - 1

const dbg = import.meta.env.DEV ? console.debug.bind(console) : () => {}

let refreshing = false

async function refreshToken() {
  if (refreshing) return
  refreshing = true
  try {
    const { token } = await authApi.refresh()
    // Mezitím mohlo dojít k odhlášení — obnovený token pak nepoužít.
    if (useAuthStore.getState().token) {
      useAuthStore.getState().replaceToken(token)
      dbg('[session] token obnoven')
    }
  } catch (err) {
    // session_expired obsloužil api/client.js; síťová chyba → zkusí se při další aktivitě.
    dbg('[session] obnova tokenu selhala:', err?.message)
  } finally {
    refreshing = false
  }
}

function checkExpired() {
  const { token, sessionExpired } = useAuthStore.getState()
  const t = tokenTimes(token)
  if (t && Date.now() >= t.exp - EXPIRY_SKEW_MS) {
    sessionExpired()
    return true
  }
  return false
}

export function useSessionWatch() {
  const token = useAuthStore(s => s.token)

  // Časovač na expiraci + kontrola při návratu na kartu.
  useEffect(() => {
    const t = tokenTimes(token)
    if (!t) return

    let timer = null
    const schedule = () => {
      const wait = t.exp - EXPIRY_SKEW_MS - Date.now()
      if (wait <= 0) { checkExpired(); return }
      timer = setTimeout(schedule, Math.min(wait, MAX_TIMEOUT_MS))
    }
    schedule()

    const onVisible = () => {
      if (document.visibilityState === 'visible') checkExpired()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [token])

  // Obnova tokenu při aktivitě uživatele.
  useEffect(() => {
    if (!token) return
    let lastCheck = 0
    const onActivity = () => {
      const now = Date.now()
      if (now - lastCheck < ACTIVITY_THROTTLE_MS) return
      lastCheck = now
      if (checkExpired()) return
      const t = tokenTimes(useAuthStore.getState().token)
      if (t && t.exp - now < (t.exp - t.iat) / 2) refreshToken()
    }
    const opts = { passive: true, capture: true }
    window.addEventListener('pointerdown', onActivity, opts)
    window.addEventListener('keydown', onActivity, opts)
    window.addEventListener('wheel', onActivity, opts)
    return () => {
      window.removeEventListener('pointerdown', onActivity, opts)
      window.removeEventListener('keydown', onActivity, opts)
      window.removeEventListener('wheel', onActivity, opts)
    }
  }, [token])
}
